import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from '@mui/material/Box';

import { size, isEqual, assign, cloneDeep, isEmpty, isNull, isUndefined, round } from "lodash";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format, isValid } from "date-fns";

import PaginationContainer from "@plannel/components/PaginationContainer"
import monthlyCategoryLocationService from "@plannel/services/ip/monthly-category-location-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import { green } from "@mui/material/colors";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import ItemTypeFilter, { ITEM_TYPE } from "@plannel/components/filter/ItemTypeFilter";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };

const TOTAL = "total";
const AVG = "avg";
const STDDEV = "stddev";
const CV = "cv";
const AGGREGATE_COLUMNS = [TOTAL, AVG, STDDEV, CV];

const MEASURE = CodeUtil.ipMeasure;
const { snackBarMessageColor } = ColorUtil;
const MonthlyByTrendDeatil = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const curreny = userInfo?.currencySymbol;

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );
  const [itemTypeFilter, setItemTypeFilter] = useState(reduxViewState?.itemType ? reduxViewState.itemType : null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [columnDefs, setColumnDefs] = useState([]);
  const exportColumnDefs = useRef([]);
  const [gridData, setGridData] = useState([]);
  const [pivotConfig, setPivotConfig] = useState({
    groupKey: "measure",
  });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(
    () => [MEASURE.SALES_QTY, MEASURE.PROFIT, MEASURE.SALES_AMT, MEASURE.STOCK_QTY, MEASURE.STOCK_AMT, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER, MEASURE.YOY_PER, MEASURE.MOM_PER],
    []
  );
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);
  const [gridLoading, setGridLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const gridRef = useRef();

  const resultTotalCount = useRef(null);
  const pageSizes = [10, 30, 50, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const EXCEL_EXPORT = 'excelExport'

  const valueFormatter = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  const defaultColumnDefs = [
    {
      headerName: "itemCd",
      showRowGroup: "itemCd",
      colId: "itemCdGrp",
      chartDataType: "category",
      width: 220,
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
    },
    { headerName: "itemCode", field: "itemCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    {
      headerName: "itemName",
      showRowGroup: "itemNm",
      colId: "itemNmGrp",
      chartDataType: "category",
      width: 220,
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
    },
    { headerName: "item", field: "itemNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    {
      headerName: "measure", field: "measure", type: ["nonEditableColumn"], 
      width: 170,
      pinned: "left", lockPinned: true,
      valueFormatter,
      headerComponent: CustomHeaderMenuIcon,
      cellRenderer: "agGroupCellRenderer",
      columnGroupShow: "closed",
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      }
    }
  ];

  const defaultExportColumnDefs = [
    { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true },
    { headerName: "itemNm", field: "itemNm", rowGroup: true, hide: true },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm" },
    { headerName: "measure", field: "measure", valueFormatter}
  ]

  const getRequestParams = (isPagination = true) => {
    const params = {};
    params.pagination = isPagination;

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (itemTypeFilter) {
      params.itemType = itemTypeFilter;
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (periodFilter?.start) {
      params.startDate = format(periodFilter.start, 'yyyy-MM-dd');
    }

    if (periodFilter?.end) {
      params.endDate = format(periodFilter.end, 'yyyy-MM-dd');
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }
  
      if (pageSize) {
        params.pageSize = pageSize;
      }
  
      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }
    params.measures = measures;

    return params;
  };

  const formatterByMeasure = (params, isExport = false) => {
    const { data, colDef } = params;
    const fieldName = colDef?.field
    const value = data?.[fieldName];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const amtColumns = [MEASURE.SALES_AMT, MEASURE.PROFIT, MEASURE.STOCK_AMT];
    const rationalColumns = [MEASURE.AVG_NO_OF_LOCATION, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER];
    const percentColumns = [MEASURE.YOY_PER, MEASURE.MOM_PER];
    const turnoverColumn = MEASURE.INV_TURNOVER;

    let digits = rationalColumns.includes(data.measure) ? 2 : 0;
    if (isEqual(CV, fieldName) || isEqual(turnoverColumn, data.measure)) {
      digits = 2;
    }

    if (percentColumns.includes(data.measure)) {
      if (isExport) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 })
      } else {
      return NumberUtil.formatNumber(value, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 0 })
      }
    }

    let formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: digits, minimumFractionDigits: digits });
    if (amtColumns.includes(data.measure)) {
      formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    }
    
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }

    if (amtColumns.includes(data.measure) && ![CV, STDDEV].includes(fieldName)) {
      return value >= 0 ? curreny + formatValue : `-${curreny}${formatValue.replace("-", "")}`;
    } 

    return formatValue;
  }
  formatterByMeasure.customType = EXCEL_EXPORT;

  const headerFormatter = (value) => {
    if (isValid(new ZDate(value))) {
      return format(new ZDate(value), "yyyy-MM");
    } else {
      return value;
    }
  }

  const setGridHeaders = (headers) => {
    if (headers.length <= 0) {
      setColumnDefs(defaultColumnDefs);
      return;
    }

    const colDefs = cloneDeep(defaultColumnDefs);
    const exportColDefs = cloneDeep(defaultExportColumnDefs);

    headers.forEach((header) => {
      colDefs.push({
        headerName: header, field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: formatterByMeasure
      });
      exportColDefs.push({
        headerName: header, field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_export_' + header,
        valueFormatter: formatterByMeasure
      });
    });
    setColumnDefs(colDefs);
    exportColumnDefs.current = exportColDefs;
  }

  const getAggData = (pivotData) => {
    const count = pivotData.length;

    if (count === 0) {
      return { total: null, avg: null, stddev: null, cv: null };
    }

    // Total & Avg
    const values = pivotData;
    const total = values.reduce((prev, current) => prev + current, 0);
    const avg = round(total / count, 5);

    // stdDev
    let powSum = 0;
    for (const value of values) {
      powSum += Math.pow(value - avg, 2);
    }

    const stddev = round(Math.sqrt(powSum / count), 5);
    const cv = avg !== 0 ? stddev / avg : 0;
    return { total, avg, stddev, cv };
  }

  const setGridDatas = (headers, results) => {
    const datas = [];

    const itemCd = "itemCd";
    const itemNm = "itemNm";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesAmtPivotData = "salesAmtPivotData";
    const stockQtyPivotData = "stockQtyPivotData";
    const stockAmtPivotData = "stockAmtPivotData";
    const invDaysPivotData = "invDaysPivotData";
    const invTurnoverPivotData = "invTurnoverPivotData";
    const profitPivotData = "profitPivotData";
    const invYoyAmtPerPivotData = "invYoyAmtPerPivotData";
    const invMomAmtPerPivotData = "invMomAmtPerPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;
      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { itemCd: resultsData[itemCd], itemNm: resultsData[itemNm],
          measure: display != null && !isEmpty(display) ? display : measureName,
          originMeasure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];
          let aggData = {};

          switch (measureName) {
            case MEASURE.SALES_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              aggData = getAggData(resultsData[salesQtyPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.SALES_AMT:
              data[header] = resultsData[salesAmtPivotData][idx];
              aggData = getAggData(resultsData[salesAmtPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.STOCK_QTY:
              data[header] = resultsData[stockQtyPivotData][idx];
              aggData = getAggData(resultsData[stockQtyPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.STOCK_AMT:
              data[header] = resultsData[stockAmtPivotData][idx];
              aggData = getAggData(resultsData[stockAmtPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.INV_DAYS:
              data[header] = resultsData[invDaysPivotData][idx];
              aggData = getAggData(resultsData[invDaysPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.INV_TURNOVER:
              data[header] = resultsData[invTurnoverPivotData][idx];
              aggData = getAggData(resultsData[invTurnoverPivotData]);
              assign(data, aggData);
              break;
          
            case MEASURE.PROFIT:
              data[header] = resultsData[profitPivotData][idx];
              aggData = getAggData(resultsData[profitPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.YOY_PER:
              data[header] = resultsData[invYoyAmtPerPivotData][idx];
              aggData = getAggData(resultsData[invYoyAmtPerPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.MOM_PER:
              data[header] = resultsData[invMomAmtPerPivotData][idx];
              aggData = getAggData(resultsData[invMomAmtPerPivotData]);
              assign(data, aggData);
              break;

            default:
            break;
          }
        }
        delete data.hrchyCd;
        datas.push(data);
      }
    }
    
    return datas;
  }

  const retrieveMonthlyTrendCategoryLocation = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();
    monthlyCategoryLocationService.getDetailAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        if (pivotResult) {
          const { localDateHeader, data } = pivotResult;
          if (pivotResult?.data && pivotResult.data.length > 0) {
            const headers = localDateHeader.map(header => headerFormatter(header));
            setPivotConfig({
              ...pivotConfig,
              include: headers
            });
            const newHeader = [...headers, ...AGGREGATE_COLUMNS];
            setGridHeaders(newHeader);
            setGridData(setGridDatas(newHeader, data));
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
            setGridHeaders([]);
            setGridData([]);
            setPivotConfig({
              ...pivotConfig,
              include: []
            });
          }
          setGridPages(totalPages);
          resultTotalCount.current = totalItems;

          const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, displayName });
          const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
          const reduxState = { ...pageState, ...filterState };
          reduxDispatch(reduxUtil.updateViewState(reduxState));
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;

    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMonthlyTrendCategoryLocation();
    }
  }
  
  const getRowStyle = params => {
    const measureOrder = size(measures) > 0 ? measures : allMeasure;
    if (params?.data?.measure && isEqual(params.data.measure, measureOrder[0])) {
      return { background: green[50] };
    }
    return null;
  };

  const retrieveP13n = () => {
    return planConfigService.getMeasureDisplayNames(CodeUtil.module.IP)
      .then(response => {
        const { data } = response;
        if (data && !isEmpty(data?.objVal)) {
          setDisplayName(data.objVal);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setGridLoading(true)
      });
  }

  useEffect(() => {
    if(!isEmpty(columnDefs)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  useEffect(() => {
    if (!isEmpty(measures) && gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, periodFilter, measures, itemTypeFilter, itemSearch.current, gridLoading]);

  useEffect(() => {
    if (!isEmpty(measures) && gridLoading) {
      retrieveMonthlyTrendCategoryLocation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize])

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveP13n()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={CodeUtil.buckets.MONTH} maxDate={MAX_END_DATE}/>
        <ItemTypeFilter viewName={viewName} itemTypeValue={itemTypeFilter} onChangeFilter={setItemTypeFilter} itemTypeFilters={[ITEM_TYPE.KIT, ITEM_TYPE.FG]}/>
        <IPItemAutocomplete retrieve={retrieveMonthlyTrendCategoryLocation} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ml: "auto"}}>
          <ExcelExportButton
            title={title}
            service={monthlyCategoryLocationService.getDetailAllForCSV}
            columnDefs={exportColumnDefs.current}
            params={{...getRequestParams(false), isPivot: true}}
            setExcelData={setGridDatas}
            headerFormatter={headerFormatter}
            isCsv
            translationColumns={["measure"]}
          />
        </Box>
      </FilterContainer>
        <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 50px)", mt: 2, mb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            {...defaultGridMemo}
            getRowStyle={getRowStyle}
            animateRows
            groupHideOpenParents
            groupDisplayType={"custom"}
            groupDefaultExpanded={-1}
          >
          </AgGridReact>
        </Box>
        <PaginationContainer
            currentPage={currentPage} totalPages={gridPages}
            pageSize={pageSize} pageSizes={pageSizes}
            setCurrentPage={setCurrentPage} setPageSize={setPageSize}
          />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        displayNames={displayName}
        hasDisplayName={true}
      />
    </>
  );
};

export default withTranslation()(MonthlyByTrendDeatil);